/**
 * The entitlement editor inside the package modal: the API catalog grouped into
 * category blocks, one row per entitlement, a checkbox for the boolean ones and
 * a number plus an unlimited toggle for the counted ones. A bounded number (the
 * monitoring interval) has no unlimited toggle and stays within its bounds, and
 * a planned row (promised, delivered by nothing yet) carries a chip saying so.
 *
 * Rows are hairline-separated on the modal's own surface rather than sitting in
 * a bordered box: `.crm-cb.is-on` marks the unlimited toggle by filling with
 * `--crm-sunk` and dropping its border, and a control standing ON a sunk surface
 * would then have neither - armed reading fainter than idle. index.css says as
 * much next to `.crm-cb.is-armed`.
 */
import { Check } from "lucide-react";
import {
  UNLIMITED,
  cn,
  featureLabel,
  type EntitlementCatalogEntry,
  type EntitlementValue,
  type Locale,
  type PlansT,
} from "@wa/ui";
import { IconBtn } from "../../../ui";
import { Pill } from "../_shared/LocaleFields";

/**
 * Label then control. The first track is `minmax(0,1fr)` so a long entitlement
 * name truncates instead of pushing the control off the row, and the second is
 * `auto` so the control keeps the width it asks for.
 */
const COLS = "minmax(0,1fr) auto";

/** Shared by both row kinds, so a boolean row and a number row line up. */
const ROW = "grid items-center gap-3 border-t border-[var(--crm-hair)] py-2 first:border-t-0";

export function FeatureMatrix({
  grouped,
  valueOf,
  onChange,
  locale,
  t,
}: {
  /** Catalog entries already grouped and ordered by the hook. */
  grouped: [string, EntitlementCatalogEntry[]][];
  valueOf: (entry: EntitlementCatalogEntry) => EntitlementValue;
  onChange: (key: string, value: EntitlementValue) => void;
  locale: Locale;
  t: PlansT;
}) {
  return (
    <div className="mt-1">
      {grouped.map(([category, entries]) => (
        <div key={category}>
          <div className="crm-lab uppercase tracking-[0.07em]">{t(`cat.${category}`)}</div>
          <div>
            {entries.map((entry) => (
              <FeatureRow
                key={entry.key}
                entry={entry}
                locale={locale}
                t={t}
                value={valueOf(entry)}
                onChange={(v) => onChange(entry.key, v)}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function FeatureRow({
  entry,
  value,
  onChange,
  locale,
  t,
}: {
  entry: EntitlementCatalogEntry;
  value: EntitlementValue;
  onChange: (v: EntitlementValue) => void;
  locale: Locale;
  t: PlansT;
}) {
  const label = (
    <span className="flex min-w-0 items-center gap-2">
      <span className="crm-trunc text-[13px]">{featureLabel(entry.key, locale)}</span>
      {entry.planned ? <span className="crm-chip shrink-0">{t("editor.planned")}</span> : null}
    </span>
  );

  // A boolean row is one big label, so the whole line toggles rather than only
  // the 17px box at the far right of a 780px modal.
  if (entry.type === "boolean") {
    return (
      <label className={cn(ROW, "cursor-pointer")} style={{ gridTemplateColumns: COLS }}>
        {label}
        <input
          type="checkbox"
          checked={value === true}
          onChange={(e) => onChange(e.target.checked)}
          className="accent-[var(--color-primary)] justify-self-end"
          style={{ flex: "none", width: 17, height: 17 }}
        />
      </label>
    );
  }

  const num = typeof value === "number" ? value : 0;
  const unlimited = num === UNLIMITED;

  if (entry.max !== undefined) {
    const lo = entry.min ?? 0;
    const hi = entry.max;
    return (
      <div className={ROW} style={{ gridTemplateColumns: COLS }}>
        {label}
        <Pill
          className="h-[38px] w-[112px] px-4"
          type="number"
          inputMode="numeric"
          min={String(lo)}
          max={String(hi)}
          value={String(num)}
          onChange={(v) => {
            const n = Math.floor(Number(v));
            onChange(Math.min(hi, Math.max(lo, Number.isFinite(n) && v !== "" ? n : lo)));
          }}
        />
      </div>
    );
  }

  return (
    <div className={ROW} style={{ gridTemplateColumns: COLS }}>
      {label}
      <span className="flex min-w-0 items-center gap-2">
        {/* Unlimited is spelled INTO the field, not just onto the button beside
            it. The shared editor blanked the input and tinted the toggle, which
            left an empty box as the only thing the eye lands on - an empty box
            reads as "nothing set", which is the opposite of no limit. The type
            flips to text for that one state because a number input cannot
            display a glyph. */}
        <Pill
          className="h-[38px] w-[112px] px-4"
          type={unlimited ? "text" : "number"}
          inputMode="numeric"
          min="0"
          disabled={unlimited}
          value={unlimited ? "∞" : String(num)}
          onChange={(v) => {
            if (v === "") return onChange(0);
            const n = Math.floor(Number(v));
            onChange(Number.isFinite(n) ? Math.max(0, n) : 0);
          }}
        />
        {/* Pressed state carried by the fill AND the tick, never by colour on
            its own: the two icons differ in shape, so the state survives a
            greyscale screen. */}
        <IconBtn
          wide
          aria-pressed={unlimited}
          className={cn(unlimited && "is-on")}
          onClick={() => onChange(unlimited ? 0 : UNLIMITED)}
        >
          {unlimited ? <Check className="size-3.5" /> : null}
          <span aria-hidden>∞</span>
        </IconBtn>
      </span>
    </div>
  );
}
